import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import PageLayout from '@components/layout/PageLayout';
import Button from './Button';
import ErrorState from './ErrorState';
import { LoadingState } from '@components/common/loading';
import { ArrowLeft } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePermission?: keyof ReturnType<typeof usePermissions>;
  requireOneOfPermissions?: (keyof ReturnType<typeof usePermissions>)[];
  fallbackPath?: string;
  showError?: boolean;
}

// Helper: Render access denied page or redirect
const AccessDenied: React.FC<{ showError: boolean; fallbackPath: string }> = ({ showError, fallbackPath }) => {
  if (showError) {
    return (
      <PageLayout>
        <ErrorState
          title="Access Denied"
          message="You don't have permission to access this page."
          action={
            <Button variant="primary" onClick={() => window.history.back()}>
              <ArrowLeft size={16} />
              Go Back
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return <Navigate to={fallbackPath} replace />;
};

// Helper: Check if user has required permission
const hasRequiredPermission = (
  permissions: ReturnType<typeof usePermissions>,
  requirePermission?: keyof ReturnType<typeof usePermissions>
): boolean => {
  if (!requirePermission) {
    return true;
  }

  const permValue = permissions[requirePermission];
  return typeof permValue === 'boolean' && permValue;
};

// Helper: Check if user has at least one of the required permissions
const hasAnyOfPermissions = (
  permissions: ReturnType<typeof usePermissions>,
  requireOneOfPermissions?: (keyof ReturnType<typeof usePermissions>)[]
): boolean => {
  if (!requireOneOfPermissions || requireOneOfPermissions.length === 0) {
    return true;
  }

  return requireOneOfPermissions.some((perm) => {
    const permValue = permissions[perm];
    return typeof permValue === 'boolean' && permValue;
  });
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePermission,
  requireOneOfPermissions,
  fallbackPath = '/dashboard',
  showError = true
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const permissions = usePermissions();

  // Still loading auth state
  if (isLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading..." spinnerSize="large" />
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions
  const hasSinglePermission = hasRequiredPermission(permissions, requirePermission);
  const hasMultiplePermissions = hasAnyOfPermissions(permissions, requireOneOfPermissions);

  if (!hasSinglePermission || !hasMultiplePermissions) {
    return <AccessDenied showError={showError} fallbackPath={fallbackPath} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
